import type { DrillingMultiAnalysisState, DrillingMultiAnalysisStateParams } from '@go-widgets/well-logs-widget';
import type { IWellSource, WellListPanelStore } from '@repo/well-list';
import { action, computed, makeObservable, observable } from 'mobx';

import { WidgetEntity } from 'src/entities/widget/WidgetEntity';
import type { SizeBoundaries, TWidgetOptions } from 'src/entities/widget/WidgetEntity';
import { requireService } from 'src/packages/di';

import type { CalculationStatusType } from './CalculationStatus';

export type TDrillingMultiAnalysisSerializedInternalState = {
  stateParams: Partial<DrillingMultiAnalysisStateParams>;
  wellId: number | null;
};

type DrillingMultiAnalysisWidgetOptions = {
  type: string;
  stateParams: Partial<DrillingMultiAnalysisStateParams>;
  wellId: number | null;
};

export class DrillingMultiAnalysisWidgetEntity extends WidgetEntity {
  readonly sizeBoundaries: SizeBoundaries = {
    minInPixels: { w: 500, h: 250 },
    maxInPixels: null,
    min: null,
    max: { w: 256, h: 100 },
  };

  @observable state: DrillingMultiAnalysisState | null = null;
  @observable type: string;
  @observable wellId: number | null;
  @observable.ref stateParams: Partial<DrillingMultiAnalysisStateParams>;
  @observable inActiveTab: boolean = false;
  @observable wellboreName: string | null = null;
  @observable corridorsCalculationStatus: CalculationStatusType | null = null;
  private readonly wellSource: IWellSource | null;

  constructor(
    { type, stateParams, wellId }: DrillingMultiAnalysisWidgetOptions,
    widgetOptions: TWidgetOptions,
    private readonly wellListPanelStore: WellListPanelStore | null,

    private readonly i18 = requireService('i18'),
  ) {
    super(widgetOptions);

    this.type = type;
    this.wellId = wellId;
    this.stateParams = stateParams;

    this.wellSource =
      this.wellListPanelStore?.getNewWellSourceInstance({
        sortByStatus: false,
        sortingType: 'nameAsc',
        filterType: null,
      }) ?? null;

    makeObservable(this);
  }

  get title(): string {
    return this.i18.t('widgets:DrillingMultiAnalysis.title', { defaultValue: 'Drilling analysis' });
  }

  get isControlWidget(): boolean {
    return false;
  }

  getNameT(): string {
    return 'widgets:DrillingMultiAnalysis.title';
  }

  @action.bound
  setWellId(wellId: number | null): void {
    this.wellId = wellId;
  }

  @action.bound
  setStateParams(stateParams: Partial<DrillingMultiAnalysisStateParams>): void {
    this.stateParams = stateParams;
  }

  @computed
  get allWells(): number[] {
    const allWells = this.wellSource?.wells.map((well) => well.id);
    return allWells ?? [];
  }

  @action.bound
  setCorridorsCalculationStatus(status: CalculationStatusType): void {
    this.corridorsCalculationStatus = status;
  }

  @action.bound
  setState(state: DrillingMultiAnalysisState | null): void {
    this.state = state;
  }

  @action.bound
  setInActiveTab(value: boolean): void {
    this.inActiveTab = value;
    this.state?.setAppParams({
      active: this.inActiveTab,
    });
  }

  @action.bound
  setFullscreen(value: boolean): void {
    this.isFullscreen = value;
    this.state?.setAppParams({
      fullScreen: value,
    });
  }

  @action.bound
  setWellboreName(wellboreName: string): void {
    this.wellboreName = wellboreName;
  }

  override destroy() {
    this.state?.destroy();
  }
}
